import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { 
  Database, ArrowRight, CheckCircle, AlertTriangle, 
  RefreshCw, Link2, FileJson, Building2, User, Mail, Key, MapPin,
  Layers, Play, Check
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function DatabaseMigration({ user, onLogout }) {
  const navigate = useNavigate();
  const [loading, setLoading] = React.useState(false);
  const [discovering, setDiscovering] = React.useState(false);
  const [databases, setDatabases] = React.useState([]);
  const [selectedDb, setSelectedDb] = React.useState(null);
  const [migrating, setMigrating] = React.useState(false);
  const [migrationResult, setMigrationResult] = React.useState(null);
  
  // For Associate Existing Data feature
  const [companies, setCompanies] = React.useState([]);
  const [selectedCompanyForAssociation, setSelectedCompanyForAssociation] = React.useState(null);
  const [associating, setAssociating] = React.useState(false);
  const [associationResult, setAssociationResult] = React.useState(null);
  const [activeTab, setActiveTab] = React.useState('associate'); // 'associate' or 'link'
  
  // Form state for linking database
  const [formData, setFormData] = React.useState({
    company_name: '',
    company_code: '',
    admin_email: '',
    admin_password: '',
    admin_name: '',
    city: '',
    state: ''
  });

  const authHeader = {
    headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
  };

  // Discover databases and fetch companies on load
  React.useEffect(() => {
    discoverDatabases();
    fetchCompanies();
  }, []);

  const fetchCompanies = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/companies`, authHeader);
      setCompanies(response.data || []);
    } catch (error) {
      console.error('Failed to fetch companies:', error);
    }
  };

  const discoverDatabases = async () => {
    try {
      setDiscovering(true);
      const response = await axios.get(`${BACKEND_URL}/api/companies/discover-databases`, authHeader);
      setDatabases(response.data.databases || []);
    } catch (error) {
      console.error('Failed to discover databases:', error);
      // Don't show error toast for this - it's expected in single-DB mode
    } finally {
      setDiscovering(false);
    }
  };

  const handleAssociateData = async () => {
    if (!selectedCompanyForAssociation) {
      toast.error('Please select a company first');
      return;
    }

    try {
      setAssociating(true);
      const response = await axios.post(
        `${BACKEND_URL}/api/companies/${selectedCompanyForAssociation.id}/associate-existing-data`,
        {},
        authHeader
      );
      
      setAssociationResult(response.data);
      toast.success(`Successfully associated data with ${selectedCompanyForAssociation.name}!`);
      
    } catch (error) {
      console.error('Association failed:', error);
      toast.error('Failed to associate data: ' + (error.response?.data?.detail || error.message));
    } finally {
      setAssociating(false);
    }
  };

  const handleSelectDatabase = (db) => {
    setSelectedDb(db);
    setMigrationResult(null);
    // Pre-fill form with suggested values
    const suggestedName = db.database_name
      .replace('autobook_', '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
    
    setFormData({
      company_name: suggestedName,
      company_code: db.database_name.replace('autobook_', ''),
      admin_email: '',
      admin_password: '',
      admin_name: 'Admin',
      city: '',
      state: ''
    });
  };

  const handleLinkDatabase = async (e) => {
    e.preventDefault();
    
    if (!selectedDb) {
      toast.error('Please select a database first');
      return;
    }

    if (!formData.company_name || !formData.admin_email || !formData.admin_password) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setMigrating(true);
      
      const payload = {
        source_database_name: selectedDb.database_name,
        company_name: formData.company_name,
        company_code: formData.company_code || undefined,
        admin_email: formData.admin_email,
        admin_password: formData.admin_password,
        admin_name: formData.admin_name,
        city: formData.city,
        state: formData.state
      };

      const response = await axios.post(
        `${BACKEND_URL}/api/companies/link-existing-database`,
        payload,
        authHeader
      );

      setMigrationResult(response.data);
      toast.success('Database linked successfully!');
      
      // Refresh database list
      discoverDatabases();
      
    } catch (error) {
      console.error('Migration failed:', error);
      toast.error('Migration failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setMigrating(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'linked':
        return <span className="px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs">Already Linked</span>;
      case 'ready':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs">Ready to Link</span>;
      default:
        return <span className="px-2 py-1 bg-gray-100 text-gray-600 rounded-full text-xs">Unknown</span>;
    }
  };

  // Check if user is super admin
  if (!user?.is_super_admin) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg text-center">
          <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
          <p className="text-gray-600 mb-4">Only Super Admins can access the Database Migration tool.</p>
          <button
            onClick={() => navigate('/')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-white to-purple-50">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Database className="w-8 h-8" />
              <div>
                <h1 className="text-2xl font-bold">Database Migration Tool</h1>
                <p className="text-indigo-200">Link existing databases to companies after redeployment</p>
              </div>
            </div>
            <button
              onClick={() => navigate('/super-admin')}
              className="px-4 py-2 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
            >
              Back to Dashboard
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto p-6">
        {/* Tab Navigation */}
        <div className="flex gap-2 mb-6">
          <button
            onClick={() => setActiveTab('associate')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'associate'
                ? 'bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50 border'
            }`}
          >
            <Layers className="w-5 h-5" />
            Associate Existing Data
            <span className="ml-1 px-2 py-0.5 bg-white/20 rounded-full text-xs">Recommended</span>
          </button>
          <button
            onClick={() => setActiveTab('link')}
            className={`px-6 py-3 rounded-lg font-medium transition-all flex items-center gap-2 ${
              activeTab === 'link'
                ? 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white shadow-lg'
                : 'bg-white text-gray-600 hover:bg-gray-50 border'
            }`}
          >
            <Link2 className="w-5 h-5" />
            Link External Database
          </button>
        </div>

        {/* Associate Existing Data Tab */}
        {activeTab === 'associate' && (
          <div className="space-y-6">
            {/* Instructions */}
            <div className="bg-green-50 border border-green-200 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                <div>
                  <h3 className="font-bold text-green-800">Associate Existing Data with a Company</h3>
                  <p className="text-sm text-green-700 mt-1">
                    Use this to assign your existing bookings, leads, stock, and other data to a company.
                    This is useful after initial setup or when migrating from a single-tenant system.
                  </p>
                  <ol className="text-sm text-green-700 mt-2 space-y-1 list-decimal list-inside">
                    <li>Select the company you want to associate data with</li>
                    <li>Click "Associate Data" to link all orphan data to this company</li>
                    <li>Company users will only see data belonging to their company</li>
                  </ol>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Left: Company Selection */}
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
                  <h2 className="font-bold text-lg">Select Company</h2>
                  <span className="text-sm text-gray-500">{companies.length} companies</span>
                </div>
                
                <div className="divide-y max-h-[400px] overflow-y-auto">
                  {companies.length === 0 ? (
                    <div className="p-8 text-center text-gray-500">
                      <Building2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No companies found</p>
                      <p className="text-sm mt-1">Create a company first from the Super Admin dashboard</p>
                    </div>
                  ) : (
                    companies.map((company) => (
                      <div
                        key={company.id}
                        onClick={() => {
                          setSelectedCompanyForAssociation(company);
                          setAssociationResult(null);
                        }}
                        className={`p-4 cursor-pointer transition-colors ${
                          selectedCompanyForAssociation?.id === company.id
                            ? 'bg-green-50 border-l-4 border-l-green-500'
                            : 'hover:bg-gray-50'
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <div className="p-2 rounded-lg bg-green-100">
                            <Building2 className="w-5 h-5 text-green-600" />
                          </div>
                          <div>
                            <p className="font-medium text-gray-800">{company.name}</p>
                            <p className="text-xs text-gray-500">
                              Code: {company.code} • {company.city || 'No city'}
                            </p>
                          </div>
                          {selectedCompanyForAssociation?.id === company.id && (
                            <Check className="w-5 h-5 text-green-600 ml-auto" />
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Right: Action Panel */}
              <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
                <div className="p-4 bg-gray-50 border-b">
                  <h2 className="font-bold text-lg">Associate Data</h2>
                </div>
                
                {!selectedCompanyForAssociation ? (
                  <div className="p-8 text-center text-gray-500">
                    <Layers className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                    <p>Select a company from the left to associate data</p>
                  </div>
                ) : (
                  <div className="p-4">
                    {/* Selected Company Info */}
                    <div className="p-4 bg-green-50 rounded-lg mb-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-green-200">
                          <Building2 className="w-6 h-6 text-green-700" />
                        </div>
                        <div>
                          <p className="font-bold text-green-800">{selectedCompanyForAssociation.name}</p>
                          <p className="text-sm text-green-600">Code: {selectedCompanyForAssociation.code}</p>
                        </div>
                      </div>
                    </div>

                    {/* What will happen */}
                    <div className="mb-4 p-3 bg-amber-50 rounded-lg border border-amber-200">
                      <p className="text-sm text-amber-800">
                        <strong>What will happen:</strong>
                      </p>
                      <ul className="text-sm text-amber-700 mt-1 space-y-1 list-disc list-inside">
                        <li>All bookings without a company will be assigned to this company</li>
                        <li>All leads, stock, service jobs will be linked</li>
                        <li>Existing users will be assigned to this company</li>
                        <li>Default roles will be created if not present</li>
                      </ul>
                    </div>

                    {/* Action Button */}
                    <button
                      onClick={handleAssociateData}
                      disabled={associating}
                      className="w-full py-3 bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-lg hover:from-green-700 hover:to-emerald-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                    >
                      {associating ? (
                        <>
                          <RefreshCw className="w-5 h-5 animate-spin" />
                          Associating Data...
                        </>
                      ) : (
                        <>
                          <Play className="w-5 h-5" />
                          Associate Data with {selectedCompanyForAssociation.name}
                        </>
                      )}
                    </button>
                  </div>
                )}

                {/* Association Result */}
                {associationResult && (
                  <div className="p-4 border-t bg-green-50">
                    <div className="flex items-start gap-3">
                      <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                      <div className="flex-1">
                        <h3 className="font-bold text-green-800">Data Associated Successfully!</h3>
                        <p className="text-sm text-green-700 mt-1">
                          {associationResult.total_documents_updated?.toLocaleString()} documents have been associated with {associationResult.company_name}
                        </p>
                        
                        {/* Details */}
                        <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                          {Object.entries(associationResult.results || {}).map(([key, value]) => (
                            typeof value === 'number' && value > 0 && (
                              <div key={key} className="flex justify-between bg-white p-2 rounded border border-green-200">
                                <span className="text-gray-600 capitalize">{key.replace(/_/g, ' ')}</span>
                                <span className="font-medium text-green-700">{value}</span>
                              </div>
                            )
                          ))}
                        </div>
                        
                        <div className="mt-3 p-2 bg-white rounded border border-green-200">
                          <p className="text-xs text-green-800">
                            <strong>Next Step:</strong> Go to the company management page to add users who can access this data.
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Link External Database Tab (Original functionality) */}
        {activeTab === 'link' && (
          <div className="space-y-6">
        {/* Instructions */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-6 h-6 text-amber-600 flex-shrink-0" />
            <div>
              <h3 className="font-bold text-amber-800">How Database Migration Works</h3>
              <ol className="text-sm text-amber-700 mt-2 space-y-1 list-decimal list-inside">
                <li>Select an existing database from the list below</li>
                <li>Fill in the company details and admin credentials</li>
                <li>Click "Link Database" - this will NOT copy or move any data</li>
                <li>Your existing data stays in place, just linked to the new company</li>
                <li>Login with the admin email/password you set to access your data</li>
              </ol>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left: Database List */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 bg-gray-50 border-b flex items-center justify-between">
              <h2 className="font-bold text-lg">Discovered Databases</h2>
              <button
                onClick={discoverDatabases}
                disabled={discovering}
                className="flex items-center gap-2 px-3 py-1.5 text-sm bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 disabled:opacity-50"
              >
                <RefreshCw className={`w-4 h-4 ${discovering ? 'animate-spin' : ''}`} />
                Refresh
              </button>
            </div>
            
            <div className="divide-y max-h-[500px] overflow-y-auto">
              {databases.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  {discovering ? 'Discovering databases...' : 'No databases found'}
                </div>
              ) : (
                databases.map((db) => (
                  <div
                    key={db.database_name}
                    onClick={() => !db.is_already_linked && handleSelectDatabase(db)}
                    className={`p-4 cursor-pointer transition-colors ${
                      db.is_already_linked 
                        ? 'bg-gray-50 cursor-not-allowed opacity-60' 
                        : selectedDb?.database_name === db.database_name
                          ? 'bg-indigo-50 border-l-4 border-l-indigo-500'
                          : 'hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-3">
                        <div className={`p-2 rounded-lg ${
                          db.is_autobook_database ? 'bg-green-100' : 'bg-gray-100'
                        }`}>
                          <Database className={`w-5 h-5 ${
                            db.is_autobook_database ? 'text-green-600' : 'text-gray-500'
                          }`} />
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{db.database_name}</p>
                          <p className="text-xs text-gray-500">
                            {db.total_documents.toLocaleString()} documents • {db.collections.length} collections
                          </p>
                        </div>
                      </div>
                      {getStatusBadge(db.status)}
                    </div>
                    
                    {/* Collection preview */}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {Object.entries(db.collection_counts || {}).slice(0, 5).map(([coll, count]) => (
                        <span key={coll} className="text-xs bg-gray-100 px-2 py-0.5 rounded">
                          {coll}: {count}
                        </span>
                      ))}
                      {Object.keys(db.collection_counts || {}).length > 5 && (
                        <span className="text-xs text-gray-500">
                          +{Object.keys(db.collection_counts).length - 5} more
                        </span>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Right: Link Form */}
          <div className="bg-white rounded-xl shadow-sm border overflow-hidden">
            <div className="p-4 bg-gray-50 border-b">
              <h2 className="font-bold text-lg">Link Database to Company</h2>
            </div>
            
            {!selectedDb ? (
              <div className="p-8 text-center text-gray-500">
                <Link2 className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Select a database from the left to link it to a company</p>
              </div>
            ) : (
              <form onSubmit={handleLinkDatabase} className="p-4 space-y-4">
                {/* Selected Database Info */}
                <div className="p-3 bg-indigo-50 rounded-lg">
                  <div className="flex items-center gap-2 text-indigo-700">
                    <Database className="w-5 h-5" />
                    <span className="font-medium">{selectedDb.database_name}</span>
                  </div>
                  <p className="text-xs text-indigo-600 mt-1">
                    {selectedDb.total_documents.toLocaleString()} documents will be linked
                  </p>
                </div>

                {/* Company Details */}
                <div className="space-y-3">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <Building2 className="w-4 h-4" /> Company Details
                  </h3>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Company Name *</label>
                    <input
                      type="text"
                      value={formData.company_name}
                      onChange={(e) => setFormData({...formData, company_name: e.target.value})}
                      placeholder="Premier Motors Pvt Ltd"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">
                        <MapPin className="w-3 h-3 inline" /> City
                      </label>
                      <input
                        type="text"
                        value={formData.city}
                        onChange={(e) => setFormData({...formData, city: e.target.value})}
                        placeholder="Mumbai"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm text-gray-600 mb-1">State</label>
                      <input
                        type="text"
                        value={formData.state}
                        onChange={(e) => setFormData({...formData, state: e.target.value})}
                        placeholder="Maharashtra"
                        className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>
                </div>

                {/* Admin Credentials */}
                <div className="space-y-3 pt-3 border-t">
                  <h3 className="font-medium text-gray-700 flex items-center gap-2">
                    <User className="w-4 h-4" /> Admin Account
                  </h3>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">Admin Name</label>
                    <input
                      type="text"
                      value={formData.admin_name}
                      onChange={(e) => setFormData({...formData, admin_name: e.target.value})}
                      placeholder="John Doe"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      <Mail className="w-3 h-3 inline" /> Admin Email (Login ID) *
                    </label>
                    <input
                      type="email"
                      value={formData.admin_email}
                      onChange={(e) => setFormData({...formData, admin_email: e.target.value})}
                      placeholder="admin@yourcompany.com"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm text-gray-600 mb-1">
                      <Key className="w-3 h-3 inline" /> Admin Password *
                    </label>
                    <input
                      type="password"
                      value={formData.admin_password}
                      onChange={(e) => setFormData({...formData, admin_password: e.target.value})}
                      placeholder="Create a secure password"
                      className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-indigo-500"
                      required
                    />
                  </div>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  disabled={migrating}
                  className="w-full py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-lg hover:from-indigo-700 hover:to-purple-700 font-medium flex items-center justify-center gap-2 disabled:opacity-50"
                >
                  {migrating ? (
                    <>
                      <RefreshCw className="w-5 h-5 animate-spin" />
                      Linking Database...
                    </>
                  ) : (
                    <>
                      <Link2 className="w-5 h-5" />
                      Link Database to Company
                    </>
                  )}
                </button>
              </form>
            )}

            {/* Migration Result */}
            {migrationResult && (
              <div className="p-4 border-t bg-green-50">
                <div className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                  <div>
                    <h3 className="font-bold text-green-800">Migration Successful!</h3>
                    <div className="mt-2 text-sm text-green-700 space-y-1">
                      <p><strong>Company:</strong> {migrationResult.company?.name}</p>
                      <p><strong>Database:</strong> {migrationResult.company?.database}</p>
                      <p><strong>Admin Email:</strong> {migrationResult.admin_user?.email}</p>
                      <p><strong>Documents Linked:</strong> {migrationResult.total_documents?.toLocaleString()}</p>
                    </div>
                    <div className="mt-3 p-2 bg-white rounded border border-green-200">
                      <p className="text-xs text-green-800">
                        <strong>Next Step:</strong> Login with <code className="bg-green-100 px-1 rounded">{migrationResult.admin_user?.email}</code> and your password to access your data.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default DatabaseMigration;
